import React, { useState, useEffect, useRef } from 'react';

import cx from 'classnames';
import { useFormik } from 'formik';
import { Scrollbars } from 'react-custom-scrollbars-2';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import styles from './AddDevice.module.scss';
import BiometricDveicesApi from '../../../../api/BiometricDevicesApi';
import SpinnerRing from '../../../../components/SpinnerRing';
import SuccessCheckmark from '../../../../components/SuccessCheckmark';
import Tooltip from '../../../../components/Tooltip';

const AddDevice = ({ onClose, onSave, saved, saving, errorMessageOnSave, selectedDevice, tenant }) => {
  const submitting = useRef(false);
  const submitPending = useRef(false);
  const isSending = saving;
  const [timeZone, setTimeZone] = useState([]);

  const [zoneLoading, setZoneLoading] = useState(false);
  const [zoneError, setZoneError] = useState('');
  const [zoneRetry, setZoneRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setZoneLoading(true);
    setZoneError('');
    BiometricDveicesApi.timeZone()
      .then((res) => {
        if (!Array.isArray(res.data?.items) || !res.data.items.every((item) => typeof item === 'string'))
          throw new Error('Invalid time zones');
        if (active) setTimeZone(res.data.items);
      })
      .catch(() => {
        if (active) setZoneError('Unable to load time zones. Please retry.');
      })
      .finally(() => {
        if (active) setZoneLoading(false);
      });
    return () => {
      active = false;
    };
  }, [zoneRetry]);

  const validate = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Name is required.';
    }

    if (!values.serialNumber) {
      errors.serialNumber = 'Serial number is required.';
    }

    if (!values.protocol) {
      errors.protocol = 'Please select a device protocol.';
    }

    if (!values.token) {
      errors.token = 'Please add a device token generated on CAMS portal.';
    }
    if (!values.timeZone) {
      errors.timeZone = 'Please select a device time zone.';
    }

    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedDevice?.name || '',
      serialNumber: selectedDevice?.serialNumber || '',
      protocol: selectedDevice?.protocol || '',
      token: selectedDevice?.token || '',
      notes: [],
      isAddedToUnbilledCharges: selectedDevice ? selectedDevice.isAddedToUnbilledCharges : false,
      timeZone: selectedDevice?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validate,
    onSubmit: async (values) => {
      if (submitting.current) return;
      submitting.current = true;

      const sentData = { ...values };

      if (selectedDevice || !sentData.notes?.[0]?.content?.trim()) delete sentData.notes;
      // Editing device details does not classify its billing history.
      if (selectedDevice) delete sentData.isAddedToUnbilledCharges;
      try {
        await onSave(!!selectedDevice, sentData);
      } finally {
        submitting.current = false;
      }
    },
  });

  return (
    <div className={styles.overfowX}>
      {isSending && <SpinnerRing />}
      {saved && <SuccessCheckmark />}
      <div>
        <div className='modal-content py-4 ps-5 position-relative'>
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              if (submitPending.current || submitting.current || saving) return;
              submitPending.current = true;
              formik.submitForm().then(
                () => {
                  submitPending.current = false;
                },
                () => {
                  submitPending.current = false;
                }
              );
            }}
          >
            {errorMessageOnSave && <div role='alert'>{errorMessageOnSave}</div>}
            {zoneError && (
              <div role='alert'>
                {zoneError}{' '}
                <button type='button' onClick={() => setZoneRetry((value) => value + 1)}>
                  Retry time zones
                </button>
              </div>
            )}
            <Scrollbars
              className='scroll-bar'
              autoHide
              renderView={({ style, ...props }) => <div {...props} style={{ ...style, overflowX: 'hidden' }} />}
              style={{ height: '62vh' }}
            >
              <input hidden='hidden' type='text' name='tenant' id='tenant' defaultValue={tenant} />
              <div className='pe-5'>
                <p className='fw-bold mb-2'>Device info</p>
                {selectedDevice && (
                  <p>
                    Added to Unbilled Charges:{' '}
                    {selectedDevice.isAddedToUnbilledCharges === true
                      ? 'YES'
                      : selectedDevice.isAddedToUnbilledCharges === false
                      ? 'NO'
                      : 'Unknown'}
                  </p>
                )}
                <div className='row pb-7'>
                  <div className={cx(styles.inputHeight, 'form-group col-12 pb-2')}>
                    <label htmlFor='name' className='label-title'>
                      Name*
                    </label>
                    <input
                      type='text'
                      name='name'
                      id='name'
                      placeholder='Name'
                      required
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`form-control newClientInputs ${
                        formik.errors.name && formik.touched.name ? 'is-invalid' : ''
                      }`}
                    />
                    <div className='invalid-feedback'>{formik.errors.name}</div>
                  </div>

                  <div className={cx(styles.inputHeight, 'form-group col-12 pb-2')}>
                    <label htmlFor='serialNumber' className='label-title'>
                      Serial Number*
                    </label>
                    <input
                      type='text'
                      name='serialNumber'
                      id='serialNumber'
                      placeholder='Serial number'
                      required
                      value={formik.values.serialNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`form-control newClientInputs ${
                        formik.errors.serialNumber && formik.touched.serialNumber ? 'is-invalid' : ''
                      }`}
                    />
                    <div className='invalid-feedback'>{formik.errors.serialNumber}</div>
                  </div>

                  <div className={cx(styles.inputHeight, 'form-group col-12')}>
                    <label htmlFor='token' className='label-title'>
                      Device token*
                      <span className='tooltip-box'>
                        <i className='icon-info text-gray ms-2' />
                        <Tooltip label='Generated on CAMS portal' />
                      </span>
                    </label>
                    <input
                      type='text'
                      name='token'
                      id='token'
                      placeholder='Device token'
                      required
                      value={formik.values.token}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`form-control newClientInputs ${
                        formik.errors.token && formik.touched.token ? 'is-invalid' : ''
                      }`}
                    />

                    <div className='invalid-feedback'>{formik.errors.token}</div>
                  </div>

                  <div className={cx(styles.inputHeight, 'form-group col-12')}>
                    <label htmlFor='protocol' className='label-title'>
                      Device Protocol*
                    </label>
                    <select
                      id='protocol'
                      name='protocol'
                      required
                      onChange={formik.handleChange}
                      value={formik.values.protocol}
                      className={`form-control newClientInputs ${
                        formik.errors.protocol && formik.touched.protocol ? 'is-invalid' : ''
                      }`}
                      disabled={selectedDevice && formik.values.isAddedToUnbilledCharges ? 'disabled' : ''}
                    >
                      <option value='' />
                      <option value='CAMS'>CAMS</option>
                      <option value='OTHER'>Other</option>
                    </select>
                    <div className='invalid-feedback'>{formik.errors.protocol}</div>
                    {formik.values.protocol !== '' && (
                      <div className={styles.costNote}>
                        <p>
                          <strong>Licence cost:</strong> SAR 240.00 / 1 Year
                        </p>
                        <p>
                          <strong>Integration cost:</strong>{' '}
                          {formik.values.protocol === 'CAMS' ? 'SAR 490.00' : 'SAR 790.00'}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className={cx(styles.inputHeight, 'form-group col-12')}>
                    <label htmlFor='timeZone' className='label-title'>
                      Device Time Zone*
                    </label>
                    <select
                      id='timeZone'
                      disabled={zoneLoading || !!zoneError}
                      name='timeZone'
                      required
                      onChange={formik.handleChange}
                      value={formik.values.timeZone}
                      className={`form-control newClientInputs ${
                        formik.errors.timeZone && formik.touched.timeZone ? 'is-invalid' : ''
                      }`}
                    >
                      {!timeZone.includes(formik.values.timeZone) && (
                        <option value={formik.values.timeZone}>{formik.values.timeZone}</option>
                      )}
                      {timeZone.map((item) => (
                        <option key={item} value={item}>
                          {item}
                        </option>
                      ))}
                    </select>
                    <div className='invalid-feedback'>{formik.errors.timeZone}</div>
                  </div>

                  {!selectedDevice && (
                    <div className={cx(styles.inputHeight, 'form-group col-12  pt-3')}>
                      <label htmlFor='content' className='label-title'>
                        Note content
                        <sup>*</sup>
                      </label>
                      <textarea
                        rows='4'
                        id='content'
                        name='notes[0].content'
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder='Note'
                        className='form-control'
                      />
                    </div>
                  )}
                </div>
              </div>
            </Scrollbars>
            <div className='d-flex justify-content-end gap-2 p-3 border-t'>
              <RoundedButton
                text='cancel'
                btnStyle='outlined'
                size='md'
                onClick={onClose}
                isDisabled={isSending || formik.isSubmitting}
              />
              <RoundedButton
                isDisabled={isSending || formik.isSubmitting || zoneLoading || !!zoneError}
                loading={isSending}
                type='submit'
                text='Save'
                btnStyle='contained'
                color='primary'
                size='md'
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDevice;
